// Aethernal2 forum: sign-in only through the website, the website's menu in the header and its footer below the
// forum (with the language dropdown, owner 2026-11-03: "move the language selection to the footer", then "the language
// switcher should just be a dropdown"), the user pill (forum avatar + forum name + the account menu, same items as the
// website's), tag names/descriptions in the visitor's language (strings: locale/*.yml, generated from the website's
// translations by web/test/unit/forum-assets.test.ts), and the composer's "Languages" button for staff (posts in several
// languages: [lang=en]...[/lang] blocks, the server renders the reader's one, src/Formatter/LangBlocks.php), discussion
// titles in several languages ("[en] Big update [it] Grande aggiornamento", src/Discussion/Titles.php: the server sends
// the reader's title; staff get one field per language in the composer and the rename dialog), and no Admin badge
// (owner 2026-11-04: "hide the admin badge, only keep the other GA CoMa GM and MOD badges").
import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import HeaderPrimary from 'flarum/forum/components/HeaderPrimary';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import LinkButton from 'flarum/common/components/LinkButton';
import Footer from 'flarum/forum/components/Footer';
import Button from 'flarum/common/components/Button';
import TextEditorButton from 'flarum/common/components/TextEditorButton';
import RenameDiscussionModal from 'flarum/forum/components/RenameDiscussionModal';
import GroupBadge from 'flarum/common/components/GroupBadge';
import Group from 'flarum/common/models/Group';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import FormModal from 'flarum/common/components/FormModal';
import Stream from 'flarum/common/utils/Stream';
import './unipill'; // the shared pill menu labels + live bell/flag counts from the website (owner 2026-11-04)
import initGamify, { gamifyExtend } from './gamify'; // forum levels, medals, game chip, Progress, Leaderboard (§18)

// Flarum 2 extenders of this extension (routes, model attributes): read by the forum as the module's `extend` export
export { gamifyExtend as extend };
const t = (key, params) => app.translator.trans(`aethernal-forum.forum.${key}`, params);
const site = () => app.forum.attribute('aethernalSiteUrl') || 'https://aethernal2.eu';

/** Sign in = the website's sign-in (the forum has no passwords of its own). */
export function signIn() {
  const here = window.location.pathname + window.location.search;
  window.location.href = `${app.forum.attribute('baseUrl')}/auth/aethernal?return=${encodeURIComponent(here)}`;
}

/** The website's five main links (same order as web/src/views/partials/header.ts; the item shop is in game only), centred
 *  on the bar exactly like the website's header (web/public/css/chrome.css); the account side (the user pill with the
 *  website's account items, or "Log in") stays on the right. */
function SiteNav() {
  const s = site();
  return m("nav", {
    className: "aet-site-nav",
    "aria-label": t('nav_primary')
  }, m("a", {
    href: `${s}/`
  }, t('nav_home')), m("a", {
    href: `${s}/download`
  }, t('nav_download')), m("a", {
    href: `${s}/rankings`
  }, t('nav_rankings')), m("a", {
    href: `${s}/wiki`
  }, t('nav_wiki')), m("a", {
    className: "is-active",
    href: `${app.forum.attribute('baseUrl')}/`,
    "aria-current": "page"
  }, t('nav_forum')));
}

/** The website's languages (web/src/i18n/index.ts LOCALES / LOCALE_NAMES); site code -> forum (Flarum) locale. */
export const LANGS = [['en', 'en', 'English'], ['it', 'it', 'Italiano'], ['fr', 'fr', 'Français'], ['de', 'de', 'Deutsch'], ['es', 'es', 'Español'], ['pt', 'pt', 'Português'], ['gr', 'el', 'Ελληνικά'], ['ro', 'ro', 'Română']];

/** The footer language links: the website's /lang sets the shared language cookie (and the website account's language)
 *  and sends the visitor back here; a signed-in forum user's own language preference is saved first. */
export function langHref(code) {
  return `${site()}/lang?lang=${code}&return=${encodeURIComponent(window.location.href)}`;
}
function switchLang(e, code, forumCode) {
  const user = app.session.user;
  if (!user) return; // the plain link does it
  e.preventDefault();
  const go = () => {
    window.location.href = langHref(code);
  };
  user.savePreferences({
    locale: forumCode
  }).then(go, go);
}

/** The footer's language dropdown (= the website's, web/src/views/partials/footer.ts langDropdown): a <details> whose
 *  button shows the current language (flag + name) and whose menu, opening upwards, lists the 8 languages. */
function LangDropdown() {
  const s = site();
  const current = app.data.locale;
  const cur = LANGS.find(([, forumCode]) => forumCode === current) || LANGS[0];
  return m("nav", {
    className: "aet-footer__lang",
    "aria-label": t('nav_lang_choose')
  }, m("details", {
    className: "aet-lang"
  }, m("summary", {
    className: "aet-lang__button",
    title: t('nav_lang_choose')
  }, m("span", {
    className: "aet-sr"
  }, t('nav_lang'), ": "), m("img", {
    className: "aet-flag",
    src: `${s}/img/flags/${cur[0]}.png`,
    alt: "",
    width: "24",
    height: "16",
    loading: "lazy"
  }), m("span", {
    className: "aet-lang__name",
    lang: cur[1]
  }, cur[2])), m("ul", {
    className: "aet-lang__panel"
  }, LANGS.map(([code, forumCode, name]) => m("li", null, m("a", {
    className: "aet-lang__item",
    href: langHref(code),
    hreflang: forumCode,
    lang: forumCode,
    rel: "nofollow",
    "aria-current": current === forumCode ? 'true' : undefined,
    onclick: e => switchLang(e, code, forumCode)
  }, m("img", {
    className: "aet-flag",
    src: `${s}/img/flags/${code}.png`,
    alt: "",
    width: "24",
    height: "16",
    loading: "lazy"
  }), m("span", null, name)))))));
}

/** Close the language dropdown on a click outside it or on Escape (the website's fx.js does the same). */
function closeLangOnOutside() {
  document.addEventListener('click', e => {
    document.querySelectorAll('details.aet-lang[open]').forEach(d => {
      if (!d.contains(e.target)) d.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('details.aet-lang[open]').forEach(d => {
      d.removeAttribute('open');
      const sum = d.querySelector('summary');
      if (sum) sum.focus();
    });
  });
}
function SiteFooter() {
  const s = site();
  const link = (path, key) => m("li", null, m("a", {
    href: `${s}${path}`
  }, t(key)));
  // same links, order and layout as the website's footer (web/src/views/partials/footer.ts)
  return m("footer", {
    className: "aet-footer"
  }, m("div", {
    className: "aet-footer__inner"
  }, m("div", {
    className: "aet-footer__legal"
  }, m("nav", {
    "aria-label": t('footer_links')
  }, m("ul", {
    className: "aet-footer__links"
  }, link('/download', 'footer_download'), link('/status', 'footer_status'), m("li", null, m("a", {
    href: `${app.forum.attribute('baseUrl')}/`
  }, t('nav_forum'))), link('/rules', 'footer_rules'), link('/privacy', 'footer_privacy'), link('/cookies', 'footer_cookies'), link('/terms-of-sale', 'footer_terms'))), m("p", {
    className: "aet-footer__fan"
  }, t('footer_fan'))), m("div", {
    className: "aet-footer__brand"
  }, m("a", {
    className: "aet-footer__logo",
    href: `${s}/`,
    "aria-label": "Aethernal2"
  }), m("p", {
    className: "aet-footer__copy"
  }, t('footer_copyright', {
    year: new Date().getUTCFullYear()
  })), LangDropdown())));
}

/** Translated tag text from locale/<code>.yml ("tags.<slug>.name|description", generated from the website's i18n),
 *  else null (the stored text is used). Raw strings, not ICU: descriptions may contain apostrophes. */
export function tagText(slug, field) {
  const v = slug ? app.translator.translations[`aethernal-forum.forum.tags.${slug}.${field}`] : undefined;
  return typeof v === 'string' && v !== '' ? v : null;
}

/** Admins and Moderators (Flarum's group 4) get the composer's "Languages" button; the [lang] blocks work for everyone. */
export function isStaff(user) {
  if (!user) return false;
  if (user.isAdmin && user.isAdmin()) return true;
  return (user.groups() || []).some(g => g && (g.id() === '1' || g.id() === '4'));
}

/** The 8 language blocks (the website's order, English first); a selection becomes the English block's text, the
 *  cursor ends inside the English block. Empty blocks are ignored when the post is shown (the reader then gets English). */
export function langSkeleton(selected = '') {
  const parts = LANGS.map(([code]) => `[lang=${code}]\n${code === 'en' ? selected : ''}\n[/lang]`);
  return {
    text: parts.join('\n\n'),
    cursor: `[lang=en]\n${selected}`.length
  };
}
function insertLangBlocks(editor) {
  if (!editor) return;
  const [start, end] = editor.getSelectionRange();
  const value = editor.el ? editor.el.value : '';
  const selected = value.slice(start, end);
  const before = start > 0 && !/\n\n$/.test(value.slice(0, start)) ? value.slice(0, start).endsWith('\n') ? '\n' : '\n\n' : '';
  const {
    text,
    cursor
  } = langSkeleton(selected);
  editor.insertBetween(start, end, before + text + '\n');
  editor.moveCursorTo(start + before.length + cursor);
}

/** The rank groups in priority order (src/Staff.php: GA > CoMa > GM > MOD > PLAYER): [{code, id}]. */
export function rankGroups() {
  const v = app.forum.attribute('aethernalGroups');
  return Array.isArray(v) ? v : [];
}

/** A rank group's name in the reader's language (locale "groups.<code>.one|other"), else null (the stored name). */
export function groupText(groupId, form) {
  const r = rankGroups().find(g => g.id === String(groupId));
  const v = r ? app.translator.translations[`aethernal-forum.forum.groups.${r.code}.${form}`] : undefined;
  return typeof v === 'string' && v !== '' ? v : null;
}

/** Badges in rank priority (GA first ... MOD), other groups after them, no Admin badge; a signed-in user in none of
 *  the staff ranks (an admin without a rank too) gets the PLAYER badge (Flarum's Member group, which users are in implicitly and which Flarum never badges). */
export function rankBadges(items, user) {
  // the Admin group is a technical group (website admins through the sign-in), not a rank: never badged
  const admin = `group${Group.ADMINISTRATOR_ID}`;
  if (items.has(admin)) items.remove(admin);
  const ranks = rankGroups();
  let staff = false;
  ranks.forEach((r, i) => {
    const key = `group${r.id}`;
    if (items.has(key)) {
      items.setPriority(key, 1000 - i * 10);
      if (r.code !== 'player') staff = true;
    }
  });
  const player = ranks.find(r => r.code === 'player');
  const group = player && app.store.getById('groups', player.id);
  if (!staff && group && group.icon() && user && user.id() && !items.has(`group${player.id}`)) {
    items.add(`group${player.id}`, m(GroupBadge, {
      group: group
    }), 1000 - ranks.indexOf(player) * 10);
  }
}

/** Title in several languages: "[en] Big update [it] Grande aggiornamento" (same rules as src/Discussion/Titles.php:
 *  codes in any case, also [lang=it], gr = el, text before the first code = English when there is no [en], empty parts
 *  missing) -> { forum code: title } (null: no codes, a plain title). */
const TITLE_CODE = /\[(?:lang=)?(en|it|fr|de|es|pt|gr|el|ro)\]/i;
export function parseTitle(raw) {
  const parts = String(raw || '').split(new RegExp(TITLE_CODE.source, 'gi'));
  if (parts.length === 1) return null;
  const clean = s => (s || '').replace(/\s+/g, ' ').trim();
  const map = {};
  for (let i = 1; i < parts.length; i += 2) {
    let code = parts[i].toLowerCase();
    if (code === 'gr') code = 'el';
    const text = clean(parts[i + 1]);
    if (text && !(code in map)) map[code] = text;
  }
  const lead = clean(parts[0]);
  if (lead && !('en' in map)) return {
    en: lead,
    ...map
  };
  return map;
}

/** { forum code: title } -> the title field's text, in the website's order (site codes: gr for Greek). One language
 *  only: just that title. */
export function titleSource(map) {
  const parts = LANGS.filter(([, f]) => map && map[f]).map(([code, f]) => [code, map[f]]);
  if (parts.length === 1) return parts[0][1];
  return parts.map(([code, text]) => `[${code}] ${text}`).join(' ');
}

/** The text to edit for a discussion's title: every language when it has several (the API's aetTitles). */
export function editableTitle(discussion) {
  const map = discussion.attribute('aetTitles');
  return map && typeof map === 'object' && Object.keys(map).length > 1 ? titleSource(map) : discussion.title();
}

/** Staff: the title in one field per language (the website's order, English first); "Done" writes the title field
 *  ("[en] ... [it] ..."; one language only: just that title). Empty fields: readers of that language get English. */
export class TitleLangsModal extends FormModal {
  oninit(vnode) {
    super.oninit(vnode);
    const raw = this.attrs.value || '';
    const map = parseTitle(raw) || (raw.trim() ? {
      en: raw.trim()
    } : {});
    this.fields = Object.fromEntries(LANGS.map(([, f]) => [f, Stream(map[f] || '')]));
  }
  className() {
    return 'aet-title-langs-modal Modal--small';
  }
  title() {
    return t('title_langs');
  }
  content() {
    return m("div", {
      className: "Modal-body"
    }, m("p", {
      className: "helpText"
    }, t('title_langs_help')), LANGS.map(([code, f, name]) => m("div", {
      className: "Form-group aet-title-lang"
    }, m("label", {
      for: `aet-title-${code}`
    }, m("img", {
      className: "aet-flag",
      src: `${site()}/img/flags/${code}.png`,
      alt: "",
      width: "24",
      height: "16",
      loading: "lazy"
    }), m("span", {
      lang: f
    }, name)), m("input", {
      id: `aet-title-${code}`,
      className: "FormControl",
      name: `title_${code}`,
      lang: f,
      maxlength: "80",
      bidi: this.fields[f],
      type: "text"
    }))), m("div", {
      className: "Form-group"
    }, m(Button, {
      className: "Button Button--primary Button--block",
      type: "submit"
    }, t('title_langs_done'))));
  }
  onsubmit(e) {
    e.preventDefault();
    const map = {};
    LANGS.forEach(([, f]) => {
      const v = this.fields[f]().replace(/\s+/g, ' ').trim();
      if (v) map[f] = v;
    });
    this.attrs.onsave(titleSource(map));
    this.hide();
  }
}
export function titleLangsButton(value, onsave) {
  return m(Button, {
    type: "button",
    className: "Button Button--icon Button--link aet-title-langs-button",
    icon: "fas fa-language",
    title: t('title_langs'),
    "aria-label": t('title_langs'),
    onclick: () => app.modal.show(TitleLangsModal, {
      value: value(),
      onsave
    }, true)
  });
}
app.initializers.add('aethernal-forum', () => {
  // rank groups: names in the reader's language, badges in rank order (+ PLAYER for everyone else)
  override('flarum/common/models/Group', 'nameSingular', function (original) {
    return groupText(this.id(), 'one') ?? original();
  });
  override('flarum/common/models/Group', 'namePlural', function (original) {
    return groupText(this.id(), 'other') ?? original();
  });
  extend('flarum/common/models/User', 'badges', function (items) {
    rankBadges(items, this);
  });

  // posts in several languages: the staff's composer button inserts the 8 blocks
  extend('flarum/common/components/TextEditor', 'toolbarItems', function (items) {
    if (!isStaff(app.session.user)) return;
    items.add('aetLangs', m(TextEditorButton, {
      icon: "fas fa-language",
      className: "Button Button--icon Button--link aet-langs-button",
      title: t('post_langs'),
      "aria-label": t('post_langs'),
      onclick: () => insertLangBlocks(this.attrs.composer.editor)
    }), 90);
  });

  // titles in several languages: staff write them in one field per language (composer + rename dialog); the rename
  // dialog starts from every language, so saving it never drops the translations
  extend('flarum/forum/components/DiscussionComposer', 'headerItems', function (items) {
    if (!isStaff(app.session.user) || !items.has('discussionTitle')) return;
    items.add('aetTitleLangs', m("h3", {
      className: "aet-title-langs"
    }, titleLangsButton(() => this.title(), raw => this.title(raw))), -10);
  });
  override(DiscussionControls, 'renameAction', function () {
    return app.modal.show(RenameDiscussionModal, {
      currentTitle: editableTitle(this),
      discussion: this
    });
  });
  extend(RenameDiscussionModal.prototype, 'content', function (vnode) {
    if (!isStaff(app.session.user)) return;
    const group = vnode && vnode.children && vnode.children[0] && vnode.children[0].children && vnode.children[0].children[0];
    if (group && Array.isArray(group.children)) group.children.push(titleLangsButton(() => this.newTitle(), raw => this.newTitle(raw)));
  });
  closeLangOnOutside();

  // tag names and descriptions in the visitor's language (forum only: the admin panel edits the stored text)
  override('ext:flarum/tags/common/models/Tag', 'name', function (original) {
    return tagText(this.slug(), 'name') ?? original();
  });
  override('ext:flarum/tags/common/models/Tag', 'description', function (original) {
    return tagText(this.slug(), 'description') ?? original();
  });

  // the site links live in the header's middle column (Header-primary; core puts nothing there, extensions' links
  // still render after ours)
  override(HeaderPrimary.prototype, 'view', function (original) {
    return m("div", {
      className: "aet-header-primary"
    }, SiteNav(), original());
  });

  // the user pill: Flarum's session menu with the website's account items (same as the website's pill: Account,
  // Security, Coins, the forum profile, Admin for the owner accounts, Log out); the button itself is styled as the
  // website's pill (less/forum.less: forum avatar + forum name + caret)
  extend('flarum/forum/components/SessionDropdown', 'items', function (items) {
    const s = site();
    items.add('aetAccount', m(LinkButton, {
      icon: "fas fa-id-card",
      href: `${s}/account`,
      external: true
    }, t('nav_account')), 140);
    items.add('aetSecurity', m(LinkButton, {
      icon: "fas fa-shield-alt",
      href: `${s}/account#security`,
      external: true
    }, t('nav_security')), 130);
    items.add('aetCoins', m(LinkButton, {
      icon: "fas fa-coins",
      href: `${s}/account#coins`,
      external: true
    }, t('nav_coins')), 120);
    if (items.has('profile')) {
      items.setContent('profile', m(LinkButton, {
        icon: "fas fa-user",
        href: app.route.user(app.session.user)
      }, t('nav_forum_profile')));
    }
    // the website's admin console for the owner accounts (the forum's admins are exactly those: DiscourseConnect admin;
    // Flarum sends adminUrl to admins only)
    if (app.forum.attribute('adminUrl')) {
      items.add('aetAdmin', m(LinkButton, {
        icon: "fas fa-tools",
        href: `${s}/admin`,
        external: true
      }, t('nav_admin')), 10);
    }
  });
  extend(HeaderSecondary.prototype, 'items', function (items) {
    // the language dropdown lives in the footer (same as the website)
    if (items.has('locale')) items.remove('locale');
    if (items.has('signUp')) items.remove('signUp');
    if (items.has('logIn')) {
      items.setContent('logIn', m(Button, {
        className: "Button Button--link",
        onclick: signIn
      }, app.translator.trans('core.forum.header.log_in_link')));
    }
  });

  // anything that asks a guest to log in or sign up (reply, start a discussion, like...) goes to the website instead
  extend('flarum/forum/components/LogInModal', 'oninit', signIn);
  extend('flarum/forum/components/SignUpModal', 'oninit', signIn);

  // password and email belong to the website account
  extend('flarum/forum/components/SettingsPage', 'accountItems', function (items) {
    if (items.has('changePassword')) items.remove('changePassword');
    if (items.has('changeEmail')) items.remove('changeEmail');
    items.add('aetAccount', m("a", {
      className: "Button aet-account-link",
      href: `${site()}/account`
    }, t('nav_account')), 100);
  });
  override(Footer.prototype, 'view', () => SiteFooter());
  initGamify();
});